import React, { useState, useEffect, SyntheticEvent, useRef } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/tr";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import AdminField from "../../layout/AdminField";
import {
  initializeNewCampaign,
  initializeCampaignData,
} from "../../utils/initializeCampaign";
import { v4 as uuid } from "uuid";
import agent from "../../api/agent";
import { toast } from "react-toastify";
import ImagePickerModal from "../../layout/ImagePickerModal";
import VideoPickerModal from "../../layout/VideoPickerModal";
import Swal from "sweetalert2";
import { calculateProbabilities } from "../../utils/calculateProbabilities";
import { basename } from "../../utils/env";
import CampaignFields from "./main/CampaignFields";
import FormCustomization from "./main/FormCustomization";
import ScreenStyleSettings from "./main/ScreenStyleSettings";
import { ScratchPrize } from "../../models/scratchPrize";

export default function ScratchWinCampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<any | null>(null);
  const [titleInput, setTitleInput] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [activeVideoField, setActiveVideoField] = useState<string | null>(null);

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [iframeSize, setIframeSize] = useState({ width: 360, height: 640 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitType, setSubmitType] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const navigate = useNavigate();
  const gameScreenUrl = `${basename}/game/${id || campaign?.id}?gameType=${campaign?.gameType || 3}`;

  useEffect(() => {
    if (id) {
      agent.Campaigns.details(id).then((response) => {
        const campaignData: any = response;
        setCampaign(initializeCampaignData(campaignData));
        setTitleInput(campaignData.title);
        console.log(campaignData);
      });
    } else {
      const newCampaign = initializeNewCampaign();
      setCampaign(newCampaign);
      setTitleInput(newCampaign.title);
    }
  }, [id]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [submitType]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleScratchPrizeChange = (
    index: number,
    field: string,
    value: string | boolean | number | null
  ) => {
    if (!campaign) return;

    setIsDirty(false);

    const newScratchPrizes = [...campaign.scratchWin.scratchPrizes];
    newScratchPrizes[index] = { ...newScratchPrizes[index], [field]: value };
    setCampaign({
      ...campaign,
      scratchWin: { ...campaign.scratchWin, scratchPrizes: newScratchPrizes },
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    setIsDirty(false);
    setCampaign({ ...campaign, [field]: value });
  };

  const handleScreenStyleChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      screenStyle: { ...campaign.screenStyle, [field]: value },
    });
  };

  const handleScratchWinChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      scratchWin: { ...campaign.scratchWin, [field]: value },
    });
  };

  const handleFormStateChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      formState: { ...campaign.formState, [field]: value },
    });
  };

  const validateForm = () => {
    let tempErrors: any = {};

    if (!titleInput) tempErrors.titleInput = "Kampanya Başlığı zorunludur";
    
    if (campaign?.scratchWin?.scratchPrizes) {
      if (campaign?.scratchWin.scratchPrizes.some((scratchPrize: any) => !scratchPrize.prizeValue)) {
        tempErrors.scratchPrizes = "Ödül değeri ödül dilimi için zorunludur.";
      }
      if (
        campaign?.scratchWin.scratchPrizes.some(
          (scratchPrize: any) => scratchPrize.probability! > 100 && scratchPrize.probability! < 0
        )
      ) {
        tempErrors.probability = "Olasılık 0'dan küçük ve 100'den büyük olamaz.";
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isDirty) {
      // If there are no changes, don't resubmit or show error
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      }
      return;
    }

    const campaignId = campaign?.id || uuid();
    const screenStyleId = campaign?.screenStyle.id || uuid();
    const scratchWinId = campaign?.scratchWin.id || uuid();
    const formStateId = campaign?.formState.id || uuid();

    const campaignData = {
      ...campaign,
      id: campaignId,
      gameType: 3,
      title: titleInput,
      startDate: (campaign!.startDate as Dayjs).toISOString(),
      endDate: (campaign!.endDate as Dayjs).toISOString(),
      collectUserData:
          campaign!.collectUserData === 1
            ? 1
            : campaign!.collectUserData === 2
              ? 2
              : campaign!.collectUserData === 3
                ? 3
                : 4,
      screenStyle: {
        ...campaign!.screenStyle,
        id: screenStyleId,
        campaignId: campaignId,
      },
      scratchWin: {
        ...campaign!.scratchWin,
        id: scratchWinId,
        campaignId: campaignId,
        stockControlRate: campaign!.scratchWin.stockControlRate
          ? campaign!.scratchWin.stockControlRate
          : null,
        probabilityDropRate: campaign!.scratchWin.probabilityDropRate
          ? campaign!.scratchWin.probabilityDropRate
          : null,
        scratchPrizes: (campaign?.scratchWin?.scratchPrizes || []).map((scratchPrize: any) => ({
          ...scratchPrize,
          id: scratchPrize.id || uuid(),
          scratchWinId: scratchWinId,
          probability: scratchPrize.probability !== null ? scratchPrize.probability : null,
          stock: scratchPrize.stock !== null ? scratchPrize.stock : null
        })),
      },
      formState: {
        ...campaign!.formState,
        id: formStateId,
        campaignId: campaignId,
      },
      wheel: {},
      puzzle: {},
      touchWin: {},
      matchWin: {},
      quiz: {},
      catchGame: {}
    };

    try {
      if (id) {
        await agent.Campaigns.update(campaignData);
      } else {
        console.log(campaignData);
        await agent.Campaigns.create(campaignData);
      }
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      } else {
        setIsDirty(true);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Error response data:", error.response.data);
      } else {
        console.error("Kampanya düzenlenirken bir hata oluştu:", error.message);
      }
    }
    setSubmitType(null);
  };

  const handleShowGameScreen = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("showGameScreen");

    if (!isLoaded) {
      setIframeSrc(gameScreenUrl);
      setIsLoaded(true);
    } else {
      setIframeSrc(null);
      setTimeout(() => setIframeSrc(gameScreenUrl), 100);
    }
  };

  const handleRegularSubmit = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("regularSubmit");
  };

  const openImagePicker = (field: string) => {
    setActiveField(field);
    setOpenModal(true);
  };

  const openVideoPicker = (field: string) => {
    setActiveVideoField(field);
    setOpenVideoModal(true);
  };

  const handleImageSelect = (url: string) => {
    if (!campaign || !activeField) return;

    if (activeField.startsWith("scratchPrize")) {
      const [_, indexStr, scratchPrizeField] = activeField.split("-");
      const index = parseInt(indexStr, 10);
      handleScratchPrizeChange(index, scratchPrizeField, url);
    } else if (["modalBg", "modalInnerBg"].includes(activeField)) {
      setCampaign({ ...campaign, [activeField]: url });
    } else if (["logo", "screenBg", "screenCenterBg"].includes(activeField)) {
      handleScreenStyleChange(activeField, url);
    } else {
      handleScratchWinChange(activeField, url);
    }

    setOpenModal(false);
  };

  const handleVideoSelect = (url: string) => {
    if (!campaign || !activeVideoField) return;
    setCampaign({ ...campaign, [activeVideoField]: url });
    setOpenVideoModal(false);
  };

  const handleCalculateProbabilities = () => {
    const stockValues = campaign.scratchWin.scratchPrizes
      .filter((scratchPrize: ScratchPrize) => scratchPrize.stock !== null) // Only consider scratchPrizes with stock values
      .map((scratchPrize: ScratchPrize) => scratchPrize.stock!); // Get the stock values

    const probabilities = calculateProbabilities(stockValues);

    const updatedScratchPrizes = campaign.scratchWin.scratchPrizes.map(
      (scratchPrize: ScratchPrize, index: number) => {
        if (scratchPrize.stock !== null) {
          return {
            ...scratchPrize,
            probability: (probabilities[index] / 100).toFixed(3),
          }; // Convert to 3 digits format for summed to 1
        }
        return scratchPrize;
      }
    );

    setCampaign({
      ...campaign,
      scratchWin: { ...campaign.scratchWin, scratchPrizes: updatedScratchPrizes },
    });
  };

  const handleDeleteGameData = () => {
    Swal.fire({
      title: "Silmek istediğinizden emin misiniz?",
      text: "Bu kampanya için kaydedilmiş tüm oyun ve oyuncu verileri silinecektir. Silme işlemini geri alamazsınız!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Tamam",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        agent.Players.deleteAll(id)
          .then(() => {
            toast.success("Tüm oyun ve oyuncu verileri silindi!");
          })
          .catch((error) => {
            toast.error("Veriler silinirken bir hata oluştu.");
          });
      }
    });
  };

  const handleResetStocks = () => {
    agent.ScratchPrizes.resetStocks(campaign.scratchWin.id)
      .then(() => {
        const updatedScratchPrizes = campaign.scratchWin.scratchPrizes.map(
          (scratchPrize: ScratchPrize, index: number) => {
            if (scratchPrize.stock !== null) {
              return {
                ...scratchPrize,
                stock: scratchPrize.initialStock,
                stockDropped: false,
              };
            }
            return scratchPrize;
          }
        );
        setCampaign({
          ...campaign,
          scratchWin: { ...campaign.scratchWin, scratchPrizes: updatedScratchPrizes },
        });
        toast.success(
          "Ödül stoklarını başlangıç değerine alma işlemiyle birlikte veriler güncellendi!"
        );
      })
      .catch((error) => {
        toast.error(
          "Ödül stokları başlangıç değerlerine alınırken bir hata oluştu."
        );
      });
  };

  const handleSizeChange = (size: string | number, shouldStartPreview = false) => {
    const parsedSize = Number(size);
    if (!Number.isNaN(parsedSize)) {
      const isLandscape = [1, 3, 5].includes(parsedSize);
      setIframeSize(isLandscape ? { width: 640, height: 360 } : { width: 360, height: 640 });

      if (shouldStartPreview && id) {
        handleShowGameScreen();
      }
      return;
    }

    if (size === "9:16") {
      setIframeSize({ width: 360, height: 640 });
    } else if (size === "16:9") {
      setIframeSize({ width: 640, height: 360 });
    }
  };

  if (!campaign) return null;

  return (
    <AdminLayout title={campaign.title || "Kazı Kazan Kampanyası Ekle"}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/campaigns")}
        sx={{ mr: 3 }}
      >
        Kampanyalara Dön
      </Button>
      {id && (
        <>
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            color="success"
            onClick={() => navigate(`/statistics/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            İstatistikler
          </Button>
          <Button
            variant="contained"
            startIcon={<VideogameAssetIcon />}
            color="warning"
            onClick={() => window.open(gameScreenUrl, "_blank")}
            sx={{ mr: 2 }}
          >
            Oyun Ekranı
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeleteGameData}
          >
            Oyun ve Oyuncu Verilerini Sil
          </Button>
        </>
      )}
      {!campaign.id && (
        <Alert sx={{ my: 3 }} severity="info">
          Temel kampanya bilgilerinizi girdikten sonra kaydetmeden önce lütfen
          yan sekmelerdeki bilgileri de düzenleyiniz. Kampanyalarınızı oluşturduktan sonra ödül
          istatistiklerini inceleyebileceğiniz <strong>İstatistikler</strong>{" "}
          butonu hemen yukarıda yer alacaktır.
        </Alert>
      )}
      <Tabs
        value={tabValue}
        className="admin-tabs"
        onChange={handleTabChange}
        aria-label="basic tabs example"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mt: 2 }}
      >
        <Tab
          label="Kampanya Alanları"
          id="simple-tab-0"
          aria-controls="simple-tabpanel-0"
        />
        <Tab
          label="Genel Görselleştirme"
          id="simple-tab-1"
          aria-controls="simple-tabpanel-1"
        />
        <Tab
          label="Kazı Kazan Alanları"
          id="simple-tab-2"
          aria-controls="simple-tabpanel-2"
        />
        <Tab
          label="Ödül Alanları"
          id="simple-tab-3"
          aria-controls="simple-tabpanel-3"
        />
        <Tab
          label="Stok Düşme Kontrolü"
          id="simple-tab-4"
          aria-controls="simple-tabpanel-4"
        />
        <Tab
          label="Form Özelleştirme"
          id="simple-tab-5"
          aria-controls="simple-tabpanel-5"
        />
      </Tabs>
      <Divider sx={{ mb: 5 }} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <CampaignFields
          campaign={campaign}
          setCampaign={setCampaign}
          errors={errors}
          setErrors={setErrors}
          titleInput={titleInput}
          setTitleInput={setTitleInput}
          handleFieldChange={handleFieldChange}
          openImagePicker={openImagePicker}
          openVideoPicker={openVideoPicker}
          tabValue={tabValue}
        />
        <ScreenStyleSettings
          tabValue={tabValue}
          tabOrder={1}
          campaign={campaign}
          handleScreenStyleChange={handleScreenStyleChange}
          handleSizeChange={handleSizeChange}
          handleShowGameScreen={handleShowGameScreen}
          openImagePicker={openImagePicker}
          id={id}
          iframeSrc={iframeSrc}
          isLoaded={isLoaded}
          iframeSize={iframeSize}
        />
        {campaign.scratchWin && (
          <>
            <div
              role="tabpanel"
              hidden={tabValue !== 2}
              id="simple-tabpanel-2"
              aria-labelledby="simple-tab-2"
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                Kazı Kazan Ekranı Ayarları
              </Typography>

              <Divider sx={{ mb: 5 }} />
              <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                Kazı Kazan Ayarları
              </Typography>
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <AdminField
                  label="Kazı Kazan Kaplama Görseli"
                  value={campaign.scratchWin.coverImage}
                  onChange={(value) =>
                    handleScratchWinChange("coverImage", value)
                  }
                  helperText="Kazınacak alanı kaplayan görseli buradan ekleyiniz"
                  type="button"
                  buttonText="Galeri'den Seç"
                  onClick={() => openImagePicker("coverImage")}
                />
              </Grid>
            </div>
            <div
              role="tabpanel"
              hidden={tabValue !== 3}
              id="simple-tabpanel-3"
              aria-labelledby="simple-tab-3"
            >
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={2}></Grid>
                <Grid item xs={10}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AnalyticsIcon />}
                    color="error"
                    onClick={handleResetStocks}
                    sx={{ mr: 2 }}
                  >
                    Stokları Başlangıç Değerlerine Al
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VideogameAssetIcon />}
                    color="warning"
                    onClick={handleCalculateProbabilities}
                  >
                    Olasılıkları Stoklardan Hesapla
                  </Button>
                </Grid>

                <Grid item xs={2}>
                  <Typography variant="subtitle2">Ödüller</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Alert sx={{ my: 3 }} severity="info">
                        Ödüllerin <strong>Olasılık</strong> değerlerini{" "}
                        <strong>0</strong> ile <strong>100</strong> arasında ve{" "}
                        ondalık değeri varsa <strong>virgül</strong> ile tek
                        basamak olarak giriniz. Değerlerin toplamı{" "}
                        <strong>100</strong>'e eşit olmalıdır.{" "}
                        <strong>Örnek:</strong> 6 adet ödül için olasılıklar;
                        10, 20, 10, 30 , 15, 15.
                      </Alert>
                      <Alert sx={{ my: 3 }} severity="error">
                        <strong>Önemli!:</strong> Eğer{" "}
                        <strong>stok kontrolü</strong> olan bir oyun
                        kurguluyorsanız yani herhangi bir ödülün stok değerini
                        girdiyseniz{" "}
                        <strong>
                          hiçbir Olasılık alanını boş bırakmayınız
                        </strong>
                        . Aksi halde stok düşüşüyle birlikte yeniden dağıtılan
                        olasılık hesapları boş bırakılan her bir alan için
                        sıfıra düşecektir. Stok sınırı yer alan oyunlarda her
                        bir ödüle olasılık değeri atamanız önemlidir.
                      </Alert>
                      <Alert sx={{ my: 3 }} severity="error">
                        <strong>Başlangıç Stoğu</strong> değeri oyunun başındaki
                        stok oranı olarak belirlenir ve{" "}
                        <strong>Stok Düşme Kontrolü</strong> ve{" "}
                        <strong>Stokları Başlangıç Değerlerine Al</strong>ma
                        özelliği için önemlidir. Bu yüzden bu alanı istisna bir
                        durum olmadıkça oyun içerisinde değiştirmeyiniz.
                      </Alert>
                      {Array.isArray(campaign.scratchWin.scratchPrizes) &&
                        campaign.scratchWin.scratchPrizes.map((scratchPrize: any, index: any) => (
                          <div key={scratchPrize.id || index}>
                            <h4>Ödül {index + 1}</h4>
                            <Grid container spacing={3}>
                              <AdminField
                                label="Ödül Adı"
                                value={scratchPrize.prizeValue}
                                fullWidth={false}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(index, "prizeValue", value)
                                }
                                error={!!errors.scratchPrizes}
                                errorMessage={errors.scratchPrizes}
                              />
                              <AdminField
                                label="Olasılık"
                                type="decimal"
                                inputProps={{
                                  step: "0.1",
                                  min: 0,
                                  max: 100,
                                  className: "probability-field",
                                }}
                                className="add-percentage"
                                value={
                                  scratchPrize.probability !== null
                                    ? parseFloat(
                                        (scratchPrize.probability * 100).toFixed(1)
                                      )
                                    : ""
                                }
                                fullWidth={false}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(
                                    index,
                                    "probability",
                                    value !== ""
                                      ? parseFloat(value) / 100
                                      : null
                                  )
                                }
                                error={!!errors.probability}
                                errorMessage={errors.probability}
                              />
                              <AdminField
                                label="Stok"
                                type="number"
                                value={scratchPrize.stock !== null ? scratchPrize.stock : ""}
                                fullWidth={false}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(
                                    index,
                                    "stock",
                                    value !== "" ? parseInt(value) : null
                                  )
                                }
                              />
                              <Grid item xs={3}></Grid>

                              <AdminField
                                label="Kart Görseli"
                                value={scratchPrize.scratchImage}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(index, "scratchImage", value)
                                }
                                helperText="Kart kazındığında çıkan kısım için görsel ekleyiniz"
                                type="button"
                                buttonText="Galeri'den Seç"
                                onClick={() =>
                                  openImagePicker(`scratchPrize-${index}-scratchImage`)
                                }
                              />
                              <AdminField
                                label="Ödül Görseli"
                                value={scratchPrize.prizeImage}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(index, "prizeImage", value)
                                }
                                helperText="Ödül için görsel ekleyiniz"
                                type="button"
                                buttonText="Galeri'den Seç"
                                onClick={() =>
                                  openImagePicker(`scratchPrize-${index}-prizeImage`)
                                }
                              />
                              <AdminField
                                label="Ödül mü?"
                                type="switch"
                                value={scratchPrize.isPrize}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(index, "isPrize", value)
                                }
                                helperText="Yalnızca pas veya İflas dilimi ise kapatınız"
                              />
                              <Grid item xs={2}></Grid>

                              <Grid item xs={1}></Grid>
                              <AdminField
                                label="Başlangıç Stoğu"
                                type="number"
                                value={
                                  scratchPrize.initialStock !== null
                                    ? scratchPrize.initialStock
                                    : ""
                                }
                                fullWidth={false}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(
                                    index,
                                    "initialStock",
                                    value !== "" ? parseInt(value) : null
                                  )
                                }
                                helperText="Stoğun oyun başındaki değeridir. Oyun içinde özel durum olmadıkça değiştirmeyiniz."
                              />
                              <AdminField
                                label="Stok Düşme Kontrolü"
                                type="switch"
                                value={scratchPrize.onStockControl}
                                inline
                                onChange={(value) =>
                                  handleScratchPrizeChange(
                                    index,
                                    "onStockControl",
                                    value
                                  )
                                }
                                helperText="Stok oranına bağlı olasılık düşüşünden etkilenecekse işaretleyiniz"
                              />
                              {scratchPrize.onStockControl && scratchPrize.stockDropped ? (
                                <>
                                  <div className="notification true">
                                    <span>Olasılık düşürüldü!</span>
                                    <small>
                                      Stoklar başlangıç değerine alınmadan dilim
                                      bir daha olasılık düşürmeden
                                      etkilenmeyecektir.
                                    </small>
                                  </div>
                                </>
                              ) : (
                                scratchPrize.onStockControl && (
                                  <div className="notification">
                                    <span>Olasılık henüz düşürülmedi</span>
                                  </div>
                                )
                              )}
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  const updatedPrizes = campaign.scratchWin.scratchPrizes.filter((_: any, i: number) => i !== index);
                                  setCampaign({
                                    ...campaign,
                                    scratchWin: {
                                      ...campaign.scratchWin,
                                      scratchPrizes: updatedPrizes,
                                    },
                                  });
                                }}
                              >
                                Sil
                              </Button>
                            </Grid>
                            <Divider sx={{ my: 3 }} />
                          </div>
                        ))}
                      <Grid item xs={12}>
                       <Button
                          variant="outlined"
                          onClick={() => {
                            const scratchWinId = campaign?.scratchWin?.id || uuid();
                            
                            setCampaign((prev : any) => ({
                              ...prev!,
                              scratchWin: {
                                ...prev!.scratchWin!,
                                id: scratchWinId,
                                campaignId: prev!.id || uuid(), // ensure the parent campaignId exists
                                scratchPrizes: [
                                  ...(prev?.scratchWin?.scratchPrizes || []),
                                  {
                                    id: uuid(),
                                    scratchWinId: scratchWinId,
                                    prizeValue: "",
                                    prizeImage: "",
                                    scratchImage: "",
                                    isPrize: true,
                                    probability: null,
                                    stock: null,
                                    initialStock: null,
                                    onStockControl: false,
                                    stockDropped: false,
                                    isCoupon: false,
                                    couponCode: "",
                                    couponUrl: "",
                                  },
                                ],
                              },
                            }));
                          }}
                        >
                          Yeni Ödül Ekle
                        </Button>
                        {errors.options && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {errors.options}
                          </Typography>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </div>
            <div
              role="tabpanel"
              hidden={tabValue !== 4}
              id="simple-tabpanel-4"
              aria-labelledby="simple-tab-4"
            >
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Alert sx={{ my: 3, mx: 12 }} severity="info">
                  Bu bölüm sadece <strong>Stok düşme kontrolü</strong> değeri
                  açık dilimlerde işe yarayacaktır. Dilimin{" "}
                  <strong>Başlangıç Stoğu</strong> değeri oyunun başındaki stok
                  oranı olarak belirlenir ve stok düştükçe bu sekmedeki{" "}
                  <strong>Stok Düşme Oranı</strong>na ulaştığında{" "}
                  <strong>Olasılık Azalma Oranı</strong> kadar olasılık
                  düşecektir. <br />
                  <br />
                  <strong>Örnek:</strong> Dilim başlangıç stoğunun 100 olduğu
                  alan için Stok 20'ye düştüğünde olaslığının yarıya düşmesini
                  istiyorsanız Stok DÜşme Oranı 20, Olaslık Azalma Oranı 50
                  olmalıdır.
                </Alert>
                <AdminField
                  label="Stok Düşme Oranı"
                  type="number"
                  value={campaign.scratchWin.stockControlRate}
                  fullWidth={false}
                  onChange={(value) =>
                    handleScratchWinChange("stockControlRate", value)
                  }
                  helperText="Dilimin başlangıç stoğunun yüzde kaç oranına düştüğünde dilim olasılığının azalacağını buradan ayarlayabilirsiniz"
                />
                <AdminField
                  label="Olasılık Azalma Oranı"
                  type="number"
                  value={campaign.scratchWin.probabilityDropRate}
                  fullWidth={false}
                  onChange={(value) =>
                    handleScratchWinChange("probabilityDropRate", value)
                  }
                  helperText="Stok yüzdesi düştüğünde olasılığının yüzde kaç oranında düşeceğini buradan ayarlayabilirsiniz. Dilimin düşen olasılık yüzdesi diğer dilimlerin olasılıklarına pay edilecektir."
                />
              </Grid>
            </div>
            <FormCustomization
              tabValue={tabValue}
              tabOrder={5}
              campaign={campaign}
              handleFormStateChange={handleFormStateChange}
            />
          </>
        )}
        <Divider />
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleRegularSubmit}
          sx={{ my: 4 }}
        >
          Kaydet
        </Button>
      </form>
      <ImagePickerModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSelect={handleImageSelect}
      />
      <VideoPickerModal
        open={openVideoModal}
        onClose={() => setOpenVideoModal(false)}
        onSelect={handleVideoSelect}
      />
    </AdminLayout>
  );
}
